// QuestionDisplayTextLogic=({
import React from 'react';
import { Text } from '../../common';
import { Heading } from '../../common';

const LogicDisplaySection = ({ logicData, formTaskSections, hide }) => {
  // Extract AND and OR logic arrays
  const andLogic = logicData?.ANDLogic || [];
  const orLogic = logicData?.ORLogic || [];
  
  // Create a single array with all logic items and their types
  const allLogicItems = [];
  
  // Add AND logic items
  andLogic.forEach(item => {
    allLogicItems.push({ ...item, logicType: 'AND' });
  });
  
  // Add OR logic items
  orLogic.forEach(item => {
    allLogicItems.push({ ...item, logicType: 'OR' });
  });
  
  if (allLogicItems.length === 0) return null;

  const findQuestion = questionId => {
    return formTaskSections
      .flatMap(section => section.questionName_array || [])
      .find(q => q.question_ID === questionId);
  };

  const findChoiceName = (question, choiceId, likert_IDAttached) => {
    if (!question || !choiceId) return '';

    // For question types 1, 2, 7, 14 (multiple choice, single choice, ranking)
    if ([1, 2, 7, 4, 14].includes(question.questionType_ID)) {
      const choice = question.choiceName_array?.find(
        c =>
          String(c.choiceName_ID) === String(choiceId) ||
          String(c.id) === String(choiceId) ||
          String(c.QuestionChoice_ID) === String(choiceId) ||
          String(c.questionChoices_ID) === String(choiceId)
      );
      return choice?.choiceName || '';
    }

    // For question type 4 (likert scale)
    if (question?.questionType_ID === 4 && likert_IDAttached) {
      const likertChoice = question?.likertScaleName_array?.find(c => c?.questionLikertScale_ID === likert_IDAttached);
      return likertChoice?.questionLikertScaleName || '';
    }

    // For question type 16 (NPS)
    if (question.questionType_ID === 16) {
      const npsChoice = question.likertScaleName_array?.findIndex(c => c.questionLikertScale_ID === likert_IDAttached);
      return npsChoice;
    }

    return '';
  };

  const findComparibilityName = (question, comparibilityId) => {
    if (!question || !comparibilityId) return '';
    const comparibility = question?.MSTCompatibility_IDs?.find(c => c?.id === comparibilityId);
    return comparibility?.Operations || '';
  };

  const renderLogicCondition = (logicItem, index) => {
    const questionId = logicItem.parentQuestionAttached || logicItem.parentQuestion_ID;
    const questionType = logicItem.parentQuestionType_ID || logicItem.parentQuestionType;
    const question = findQuestion(questionId);
    if (!question) return null;

    // Extract values
    let choiceId,
      choiceName = '',
      likertValue = '',
      comparibilityName = '',
      value = '';

    // Choice-based (1,2,7,14)
    if ([1, 2, 7, 14, 4].includes(questionType)) {
      if (logicItem.questionAttached && logicItem.questionAttached[question.question_ID]) {
        choiceId = logicItem.questionAttached[question.question_ID][0];
      } else if (logicItem.choice_ID) {
        choiceId = logicItem.choice_ID;
      }
      if (questionType === 14) {
        choiceId = logicItem.rank_IDAttached || logicItem.choice_ID || choiceId;
      }
      choiceName = findChoiceName(question, choiceId);
    }

    // Likert/NPS (4,16)
    if ([4, 16].includes(questionType)) {
      if (logicItem.questionAttached && logicItem.questionAttached[question.question_ID]) {
        choiceId = logicItem.questionAttached[question.question_ID][0];
      } else if (logicItem.choice_ID) {
        choiceId = logicItem.choice_ID;
      }
      // Try to get likert value from likert_IDAttached or from the attached array
      let likertId = logicItem.likert_IDAttached || choiceId;
      if (question.likertScaleName_array && questionType === 4) {
        const likertObj = question.likertScaleName_array.find(c => c.questionLikertScale_ID === likertId);
        likertValue = likertObj ? likertObj.questionLikertScaleName : '';
      }

      choiceName = findChoiceName(question, choiceId, likertId);
    }

    // Value/Ranking (3,11,14)
    if ([3, 11, 14].includes(questionType)) {
      comparibilityName = findComparibilityName(question, logicItem.comparibility_ID);
      value = logicItem.comparibility_value || logicItem.rank_IDAttached || '';
    }

    // Text (5,6)
    if ([5, 6].includes(questionType)) {
      value = logicItem.ifContains || '';
    }

    // Build display
    return (
      <div key={`logic-${index}`} className="d-flex" style={{ flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <Text
          className="mb-0 pb-0 mt-0 pr-3"
          fontSize={'0.8rem'}
          color={'var(--quadColor)'}
          fontWeight={'100'}
          type="5"
        >
          If
        </Text>
        <Text
          className="mb-0 pb-0 mt-0 pr-3"
          type="4"
          fontSize={'0.8rem'}
          fontWeight={'600'}
          color={'var(--paraColor)'}
        >
          {question.questionName}
        </Text>
        <Text
          className="mb-0 pb-0 mt-0 pr-3"
          fontSize={'0.8rem'}
          color={'var(--quadColor)'}
          fontWeight={'100'}
          type="5"
        >
          Is
        </Text>
        {choiceName && (
          <Text
            className="mb-0 pb-0 mt-0 pr-3"
            fontSize={'0.8rem'}
            type="5"
            color={'var(--paraColor)'}
            fontWeight={'600'}
          >
            {choiceName}
          </Text>
        )}
        {likertValue && (
          <Text
            className="mb-0 pb-0 mt-0 pr-3"
            fontSize={'0.8rem'}
            type="5"
            color={'var(--paraColor)'}
            fontWeight={'600'}
          >
            {likertValue}
          </Text>
        )}
        {logicItem?.parentQuestionType === 14 && (
          <Text className="mb-0 pb-0 mt-0 pr-3" fontSize="0.8rem" color="var(--quadColor)" fontWeight="100" type="5">
            Ranked
          </Text>
        )}
        {comparibilityName && (
          <Text
            className="mb-0 pb-0 mt-0 pr-3"
            fontSize={'0.8rem'}
            type="5"
            color={'var(--paraColor)'}
            fontWeight={'600'}
          >
            {comparibilityName}
          </Text>
        )}
        {value && (
          <Text
            className="mb-0 pb-0 mt-0 pr-3"
            fontSize={'0.8rem'}
            type="5"
            color={'var(--paraColor)'}
            fontWeight={'600'}
          >
            {value}
          </Text>
        )}

        {logicItem?.parentQuestionType !== 5 && logicItem?.parentQuestionType !== 6 && (
          <Text className="mb-0 pb-0 mt-0 pr-3" fontSize="0.8rem" color="var(--quadColor)" fontWeight="100" type="5">
            Is Selected
          </Text>
        )}

        {(logicItem?.parentQuestionType === 5 || logicItem?.parentQuestionType === 6) && (
          <Text className="mb-0 pb-0 mt-0 pr-3" fontSize="0.8rem" color="var(--quadColor)" fontWeight="100" type="5">
            If Contains
          </Text>
        )}
      </div>
    );
  };

  // Sort all logic items by parentSequence in ascending order
  const sortedLogic = [...allLogicItems].sort((a, b) => {
    if (a.parentSequence && b.parentSequence) {
      return a.parentSequence - b.parentSequence;
    }
    return 0;
  });

  return (
    <div>
      {sortedLogic.map((logicItem, index) => {
        return (
          <div key={`${logicItem.logicType}-${index}`}>
            {index !=0 && 
            <Heading className="mt-3" type="6" fontWeight="700" color="#728F01">
              {logicItem.logicType}
            </Heading>
      }
            {renderLogicCondition(logicItem, index)}
          </div>
        );
      })}
    </div>
  );
};
export default LogicDisplaySection;
